<template>
  <div class="ds-page" v-if="!$store.getters.isForbidden">
    <div class="page-header d-print-none">
      <div class="row align-items-center">
        <div class="col">
          <div class="page-pretitle">Company Client</div>
          <h2 class="page-title">Employee Directory</h2>
          <p class="page-subtitle">Manage WPS recipients, banking data, and bulk import for this company client.</p>
        </div>
        <div class="col-auto ms-auto">
          <div class="employee-page__actions">
            <button class="ds-btn ds-btn--secondary" @click="getListEmployee">
              <i class="el-icon-refresh"></i>
              Refresh
            </button>
            <button class="ds-btn ds-btn--secondary" @click="handlerBtnImport">
              <font-awesome-icon icon="file-import" />
              Import Employee
            </button>
            <button class="ds-btn ds-btn--secondary" @click="handlerBtnImportToDelete">
              <font-awesome-icon icon="trash-alt" />
              Import Delete
            </button>
            <button class="ds-btn ds-btn--blue" @click="handlerBtnAddEmployee">
              <font-awesome-icon icon="user-plus" />
              Add Employee
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="ds-card">
      <div class="employee-page__card-head">
        <div>
          <div class="employee-page__card-title">Employee List</div>
          <div class="employee-page__card-subtitle">Operational view for company-client employee WPS data.</div>
        </div>
      </div>

      <div class="employee-page__filter-grid">
        <div class="ds-field">
          <label class="ds-field__label">Search</label>
          <el-input
            v-model.trim="filters.search_value"
            size="small"
            placeholder="Search full name, email, bank name, or bank account"
            clearable
            @keyup.enter.native="applyFilters"
          />
          <div class="employee-page__search-hint">
            Better use bank account number for search. Full name, phone number, and email need exact value.
          </div>
        </div>
        <div class="employee-page__show-deleted">
          <el-checkbox v-model="filters.show_deleted" @change="applyFilters">Show Deleted</el-checkbox>
        </div>
        <div class="employee-page__filter-actions">
          <button class="ds-btn ds-btn--secondary ds-btn--sm" @click="resetFilters">Reset</button>
          <button class="ds-btn ds-btn--primary ds-btn--sm" :disabled="loading.table" @click="applyFilters">Apply</button>
        </div>
      </div>

      <div class="ds-table-wrap">
        <table class="ds-table" v-loading="loading.table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Contact</th>
              <th>Banking</th>
              <th class="text-right">Salary</th>
              <th>Status</th>
              <th class="text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in visibleList" :key="item.id">
              <td>
                <div class="ds-user-cell">
                  <div class="ds-avatar" :class="dsAvatarVariant(item?.full_name)">
                    {{ dsGetInitials(item?.full_name) }}
                  </div>
                  <div>
                    <div class="ds-user-cell__name">{{ item.full_name || "—" }}</div>
                    <div class="ds-user-cell__sub">ID #{{ item.id || "-" }}</div>
                  </div>
                </div>
              </td>
              <td>
                <div class="employee-page__stack">
                  <span>{{ item.email || "—" }}</span>
                  <span class="employee-page__muted">{{ item.phone_number || "—" }}</span>
                </div>
              </td>
              <td>
                <div class="employee-page__stack">
                  <span>{{ item.bank_name || "—" }}</span>
                  <span class="employee-page__muted">{{ item.bank_account || "—" }}</span>
                </div>
              </td>
              <td class="text-right">{{ formatCurrency(item.salary) }}</td>
              <td>
                <span class="ds-pill" :class="statusPillClass(item.user_status)">
                  {{ statusLabel(item.user_status) }}
                </span>
              </td>
              <td class="text-center">
                <el-dropdown @command="handleCommand($event, item)" trigger="hover" popper-class="employee-page__action-popper">
                  <button class="ds-btn ds-btn--secondary ds-btn--sm employee-page__action-trigger">
                    Actions <i class="el-icon-arrow-down el-icon--right"></i>
                  </button>
                  <el-dropdown-menu slot="dropdown" class="employee-page__action-menu">
                    <el-dropdown-item command="edit">
                      <div class="employee-page__action-item">
                        <span class="employee-page__action-icon"><font-awesome-icon :icon="['fas', 'pen']" /></span>
                        <span>Edit Employee</span>
                      </div>
                    </el-dropdown-item>
                    <el-dropdown-item command="delete">
                      <div class="employee-page__action-item employee-page__action-item--danger">
                        <span class="employee-page__action-icon"><font-awesome-icon icon="trash" /></span>
                        <span>Delete</span>
                      </div>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </td>
            </tr>
          </tbody>
        </table>

        <el-empty v-if="!totalVisibleRows" description="No employee data found" :image="emptyStateImage" class="ds-empty" />
      </div>

      <pagination
        :page="page"
        :per_page="per_page"
        :list="visibleList"
        :total_rows="totalVisibleRows"
        @pageChange="pageChangeHandler"
      />
    </div>

    <create-company-client-employee
      :is-shown="showModalEmployee"
      :mode="formMode"
      :client-data="selectedEmployee"
      @onHide="
        showModalEmployee = false;
        getListEmployee();
      "
    />

    <b-modal v-model="showModalImport" size="lg" title="Import Employee" hide-footer @hide="closeModalImportUser">
      <div class="employee-import-modal">
        <div class="employee-import-modal__intro">
          Upload a CSV file to add multiple employees into this company client in one step.
        </div>
        <div class="ds-field">
          <label class="ds-field__label">CSV File</label>
          <b-form-file
            v-model="selected_file.csv"
            @change="onChangeFile"
            accept=".csv"
            class="border-1 p-2"
            ref="fileInput"
            placeholder="Choose a file or drop it here..."
            drop-placeholder="Drop file here..."
          />
          <div class="employee-import-modal__meta">
            <span>* only `.csv` files are allowed</span>
            <a href="/sample/csv_employee.csv" download>
              <font-awesome-icon icon="download" />
              Download example file
            </a>
          </div>
        </div>
        <div class="employee-import-modal__actions">
          <button class="ds-btn ds-btn--secondary ds-btn--sm" @click="closeModalImportUser">Cancel</button>
          <button class="ds-btn ds-btn--primary ds-btn--sm" :disabled="loading.import_user" @click="confirmImportUser">
            Submit
          </button>
        </div>
      </div>
    </b-modal>

    <b-modal v-model="showModalImportToDelete" size="lg" title="Delete Employee" hide-footer @hide="closeModalImportUser">
      <div class="employee-import-modal">
        <div class="employee-import-modal__intro employee-import-modal__intro--warning">
          Upload a CSV file to remove multiple employees from this company client.
        </div>
        <div class="ds-field">
          <label class="ds-field__label">CSV File</label>
          <b-form-file
            v-model="selected_file.csv"
            @change="onChangeFile"
            accept=".csv"
            class="border-1 p-2"
            ref="fileInputDelete"
            placeholder="Choose a file or drop it here..."
            drop-placeholder="Drop file here..."
          />
          <div class="employee-import-modal__meta">
            <span>* only `.csv` files are allowed</span>
            <a href="/sample/csv_employee.csv" download>
              <font-awesome-icon icon="download" />
              Download example file
            </a>
          </div>
        </div>
        <div class="employee-import-modal__actions">
          <button class="ds-btn ds-btn--secondary ds-btn--sm" @click="closeModalImportUser">Cancel</button>
          <button class="ds-btn ds-btn--danger ds-btn--sm" :disabled="loading.import_user" @click="deleteImportedUser">
            Submit Delete
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  IMPORT_COMPANY_CLIENT_EMPLOYEES_TO_DELETE,
  IMPORT_COMPANY_CLIENT_EMPLOYEE_BASE64,
  DELETE_COMPANY_CLIENT_EMPLOYEE_RECORD,
  GET_COMPANY_CLIENT_EMPLOYEE_LIST,
} from "@/store/modules/company_clients";
import emptyStateImage from "@/assets/images/empty-state.png";
import Pagination from "@/ui/components/Pagination.vue";
import CreateCompanyClientEmployee from "./employee/CreateCompanyClientEmployee.vue";
import { getEmployeeStatusMeta } from "@/library/employee-status";
import popupErrorMessages from "@/library/popup-error-messages";

export default {
  components: {
    Pagination,
    CreateCompanyClientEmployee,
  },
  name: "CompanyClientEmployee",
  metaInfo: {
    title: "Company Client Employee",
  },
  computed: {
    visibleList() {
      return this.list;
    },
    totalVisibleRows() {
      return this.total_rows;
    },
  },
  data() {
    return {
      showModalEmployee: false,
      showModalImport: false,
      showModalImportToDelete: false,
      formMode: "create",
      selectedEmployee: null,
      emptyStateImage,
      list: [],
      total_rows: 0,
      per_page: 20,
      page: 1,
      size: 20,
      filters: {
        search_value: "",
        show_deleted: false,
      },
      loading: {
        table: false,
        import_user: false,
      },
      selected_file: {
        csv: null,
        csv_base64: null,
      },
    };
  },
  methods: {
    getImportSummary(payload) {
      const body = payload?.data || payload || {};
      return body?.data || body?.rows?.[0] || {};
    },
    async showImportResult(payload, actionLabel = "imported") {
      const summary = this.getImportSummary(payload);
      const total = Number(summary?.total || 0);
      const created = Number(summary?.created || 0);
      const updated = Number(summary?.updated || 0);
      const failed = Number(summary?.failed || 0);
      const failedDetails = Array.isArray(summary?.failed_details) ? summary.failed_details : [];

      if (!failed) {
        this.$message({
          title: "Success",
          type: "success",
          message: `${created + updated || total} users ${actionLabel} successfully.`,
        });
        return;
      }

      const detailPreview = failedDetails
        .slice(0, 5)
        .map((item) => `<li><strong>${item.bank_account || "-"}</strong>: ${item.message || "-"}</li>`)
        .join("");
      const summaryText = [total ? `Total: ${total}` : null, `Created: ${created}`, `Updated: ${updated}`, `Failed: ${failed}`]
        .filter(Boolean)
        .join("<br>");

      await this.$alert(
        `
          <div class="employee-import-result">
            <div>${summaryText}</div>
            ${detailPreview ? `<ul>${detailPreview}</ul>` : ""}
          </div>
        `,
        failed === total && created === 0 && updated === 0 ? "Import Failed" : "Import Completed With Notes",
        {
          dangerouslyUseHTMLString: true,
          confirmButtonText: "OK",
        }
      );
    },
    formatCurrency(value) {
      return `Rp ${Number(value || 0).toLocaleString("id-ID")}`;
    },
    statusLabel(status) {
      return getEmployeeStatusMeta(status).label;
    },
    statusPillClass(status) {
      return getEmployeeStatusMeta(status).color;
    },
    async applyFilters() {
      this.page = 1;
      await this.getListEmployee();
    },
    async resetFilters() {
      this.filters = {
        search_value: "",
        show_deleted: false,
      };
      this.page = 1;
      await this.getListEmployee();
    },
    async onChangeFile(event) {
      const file = event.target.files[0];
      if (!file) {
        this.selected_file.csv = null;
        this.selected_file.csv_base64 = null;
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        this.selected_file.csv = file;
        this.selected_file.csv_base64 = reader.result;
      };
      reader.readAsDataURL(file);
    },
    async confirmImportUser() {
      if (!this.selected_file.csv_base64) {
        this.$message.error("Please select a CSV file first.");
        return;
      }

      try {
        this.loading.import_user = true;
        const response = await this.$store.dispatch(IMPORT_COMPANY_CLIENT_EMPLOYEE_BASE64, {
          id: this.$route.params.id,
          csv_base64: this.selected_file.csv_base64,
        });

        await this.showImportResult(response, "imported");
        this.closeModalImportUser();
        await this.getListEmployee();
      } catch (err) {
        popupErrorMessages(err?.response?.data || err);
      } finally {
        this.loading.import_user = false;
      }
    },
    async deleteImportedUser() {
      if (!this.selected_file.csv_base64) {
        this.$message.error("Please select a CSV file first.");
        return;
      }

      try {
        this.loading.import_user = true;
        const response = await this.$store.dispatch(IMPORT_COMPANY_CLIENT_EMPLOYEES_TO_DELETE, {
          id: this.$route.params.id,
          csv_base64: this.selected_file.csv_base64,
        });
        await this.showImportResult(response, "deleted");
        this.closeModalImportUser();
        await this.getListEmployee();
      } catch (err) {
        popupErrorMessages(err?.response?.data || err);
      } finally {
        this.loading.import_user = false;
      }
    },
    closeModalImportUser() {
      this.showModalImport = false;
      this.showModalImportToDelete = false;
      this.selected_file = {
        csv: null,
        csv_base64: null,
      };
    },
    handleCommand(command, item) {
      const actions = {
        edit: () => this.handlerBtnUpdateCompanyClientEmployee(item),
        delete: () => this.handlerBtnDelete(item),
      };
      const action = actions[command];
      if (action) {
        action();
      }
    },
    async getListEmployee() {
      this.loading.table = true;
      try {
        const { id } = this.$route.params;
        await this.$store.dispatch(GET_COMPANY_CLIENT_EMPLOYEE_LIST, {
          id,
          page: this.page,
          size: this.size,
          per_page: this.per_page,
          ...(this.filters.search_value
            ? {
                search: this.filters.search_value,
              }
            : {}),
          show_deleted: this.filters.show_deleted ? 1 : 0,
        });
        const response = this.$store.getters.company_client_employees || {};
        this.list = response.rows || [];
        this.total_rows = response.count || 0;
      } catch (err) {
        popupErrorMessages(err?.response?.data || err);
      } finally {
        this.loading.table = false;
      }
    },
    async pageChangeHandler(page) {
      this.page = page;
      await this.getListEmployee();
    },
    async handlerBtnDelete(item) {
      this.$confirm("You will delete the employee. Continue?", "Confirmation", {
        type: "info",
        beforeClose: async (action, instance, done) => {
          if (action === "confirm") {
            instance.confirmButtonLoading = true;
            try {
              await this.$store.dispatch(DELETE_COMPANY_CLIENT_EMPLOYEE_RECORD, {
                id: item.company_client_id,
                employee_id: item.id,
                data: {},
              });
              this.$message({
                title: "Success",
                type: "success",
                message: "Employee deleted",
              });
              await this.getListEmployee();
            } catch (err) {
              popupErrorMessages(err?.response?.data || err);
            }
            instance.confirmButtonLoading = false;
          }
          done();
        },
      }).catch(() => {});
    },
    handlerBtnAddEmployee() {
      this.formMode = "create";
      this.selectedEmployee = null;
      this.showModalEmployee = true;
    },
    handlerBtnImport() {
      this.showModalImport = true;
    },
    handlerBtnImportToDelete() {
      this.showModalImportToDelete = true;
    },
    handlerBtnUpdateCompanyClientEmployee(item) {
      this.formMode = "edit";
      this.selectedEmployee = item;
      this.showModalEmployee = true;
    },
  },
  mounted() {
    this.getListEmployee();
  },
};
</script>

<style scoped>
.employee-page__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.employee-page__card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 6px;
}

.employee-page__card-title {
  font-size: 15px;
  font-weight: 700;
  color: #182433;
}

.employee-page__card-subtitle {
  font-size: 12px;
  color: #656d77;
  margin-top: 4px;
}

.employee-page__filter-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 160px auto;
  gap: 12px;
  padding: 8px 20px 18px;
  align-items: end;
}

.employee-page__show-deleted {
  display: flex;
  align-items: center;
  min-height: 32px;
  padding-bottom: 4px;
}

.employee-page__filter-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.employee-page__stack {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.employee-page__muted {
  color: #656d77;
  font-size: 12px;
}

.employee-page__search-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #656d77;
}

.employee-page__action-trigger {
  min-width: 108px;
  justify-content: center;
}

.employee-page__action-menu {
  padding: 8px;
}

.employee-page__action-item {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 170px;
  color: #1f2937;
}

.employee-page__action-item--danger {
  color: #c0392b;
}

.employee-page__action-icon {
  width: 18px;
  text-align: center;
}

.employee-import-modal__intro {
  margin-bottom: 16px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(32, 107, 196, 0.08);
  color: #304152;
  font-size: 13px;
}

.employee-import-modal__intro--warning {
  background: rgba(245, 158, 11, 0.12);
}

.employee-import-modal__meta {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
  font-size: 12px;
  color: #656d77;
  flex-wrap: wrap;
}

.employee-import-modal__meta a {
  color: #206bc4;
  text-decoration: none;
}

.employee-import-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
}

:deep(.employee-import-result) {
  font-size: 13px;
  color: #304152;
  line-height: 1.5;
}

:deep(.employee-import-result ul) {
  margin: 12px 0 0;
  padding-left: 18px;
}

@media (max-width: 900px) {
  .employee-page__actions {
    justify-content: flex-start;
  }

  .employee-page__filter-grid {
    grid-template-columns: 1fr;
  }

  .employee-page__filter-actions {
    justify-content: flex-start;
  }

  .employee-import-modal__meta {
    flex-direction: column;
  }
}
</style>
